import { useCallback, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { updateUser } from "../../api/directory";
import { useAuth } from "../../context/AuthContext";
import { localName } from "../../lib/localName";

const emptyPersonal = {
  nationality: "",
  birth_date: "",
  gender: "",
  marital_status: "",
  national_id: "",
  national_id_expiry: "",
  national_id_file: "",
  passport_number: "",
  passport_expiry: "",
  passport_file: "",
  address: "",
  emergency_name: "",
  emergency_phone: "",
};

const initialState = (initial) => ({
  name: initial?.name || "",
  name_ar: initial?.name_ar || "",
  role: initial?.role || "employee",
  employment_type: initial?.employment_type || "employee",
  job_title: initial?.job_title || "",
  phone: initial?.phone || "",
  basic_salary: initial?.basic_salary ?? "",
  allowances: {
    housing: initial?.allowances?.housing ?? 0,
    transport: initial?.allowances?.transport ?? 0,
    other: initial?.allowances?.other ?? 0,
  },
  personal: { ...emptyPersonal, ...(initial?.personal || {}) },
  allow_weekend_work: !!initial?.allow_weekend_work,
  work_mode: initial?.work_mode || "onsite",
  branch_absolute: initial?.branch_absolute !== false,
  branch_ids: initial?.branch_ids || [],
});

const payloadOf = (form) => ({
  ...form,
  basic_salary: form.basic_salary === "" ? null : Number(form.basic_salary),
  personal: Object.fromEntries(Object.entries(form.personal).map(([k, v]) => [k, v === "" ? null : v])),
});

export const useEmployeeForm = (initial, onSaved) => {
  const { t } = useTranslation();
  const { user: me, patchUser } = useAuth();
  const [form, setForm] = useState(() => initialState(initial));
  const [busy, setBusy] = useState(false);
  const snapshot = useRef(JSON.stringify(initialState(initial)));

  const persist = useCallback(async () => {
    const updated = await updateUser(initial.id, payloadOf(form));
    // تعديل المستخدم لنفسه: حدِّث جلسته فوراً كي تعكس اللوحة الرئيسية نوع العمل الجديد دون إعادة دخول.
    if (me && initial?.id === me.id) {
      patchUser({ work_mode: updated.work_mode, remote_since: updated.remote_since ?? null, role: updated.role });
    }
    toast.success(t("employees.created"));
  }, [form, initial?.id, t, me, patchUser]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await persist();
      onSaved();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail?.code) toast.error(t(`personal.errors.${detail.code}`, { ...detail, defaultValue: t("errors.generic") }));
      else toast.error(t("errors.generic"));
    } finally {
      setBusy(false);
    }
  };

  const setField = (key, value) => setForm((f) => ({ ...f, [key]: value }));
  const setPersonal = (key, value) => setForm((f) => ({ ...f, personal: { ...f.personal, [key]: value } }));
  const toggleBranch = (id) =>
    setForm((f) => ({
      ...f,
      branch_ids: f.branch_ids.includes(id) ? f.branch_ids.filter((b) => b !== id) : [...f.branch_ids, id],
    }));

  return { form, busy, submit, persist, setField, setPersonal, toggleBranch, dirty: JSON.stringify(form) !== snapshot.current };
};
